import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface Run {
  id: string;
  user_id: string;
  title?: string;
  equipment_type: 'suv' | 'truck' | 'sxs' | 'atv' | 'dirt_bike' | 'other';
  start_time?: string;
  end_time?: string;
  duration_minutes?: number;
  distance_miles?: number;
  geometry: any;
  source?: string;
  gpx_file_url?: string;
  verified_status?: 'unverified' | 'trusted' | 'suspect';
  valid_for_rebate?: boolean;
  processing_status?: 'pending' | 'processing' | 'completed' | 'error';
  processing_error?: string;
  photo_urls?: any; // JSONB array from database
  vehicle_id?: string;
  created_at: string;
}

export interface RunSegment {
  id: string;
  run_id: string;
  trail_segment_id: string;
  segment_order: number;
  equipment_type: 'suv' | 'truck' | 'sxs' | 'atv' | 'dirt_bike' | 'other';
  start_time?: string;
  end_time?: string;
  duration_minutes?: number;
  distance_miles?: number;
  obstacles_encountered?: string[];
  conditions_reported?: string;
  created_at: string;
}

export const useRuns = (userId?: string, sortBy: 'created_at' | 'start_time' = 'created_at') => {
  const [runs, setRuns] = useState<Run[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRuns = async () => {
      try {
        setIsLoading(true);
        setError(null);

        let query = supabase
          .from('runs')
          .select('*')
          .order(sortBy, { ascending: false, nullsFirst: false });

        if (userId) {
          query = query.eq('user_id', userId);
        }

        const { data, error: fetchError } = await query;

        if (fetchError) {
          throw fetchError;
        }

        setRuns(data || []);
      } catch (err) {
        console.error('Error fetching runs:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch runs');
      } finally {
        setIsLoading(false);
      }
    };

    fetchRuns();
  }, [userId, sortBy]);

  return { runs, isLoading, error };
};

export const useRunById = (runId: string | undefined) => {
  const [run, setRun] = useState<Run | null>(null);
  const [segments, setSegments] = useState<RunSegment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!runId) {
      setIsLoading(false);
      return;
    }

    const fetchRunDetails = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Fetch run
        const { data: runData, error: runError } = await supabase
          .from('runs')
          .select('*')
          .eq('id', runId)
          .maybeSingle();

        if (runError) {
          throw runError;
        }

        setRun(runData);

        // Fetch run segments
        if (runData) {
          const { data: segmentsData, error: segmentsError } = await supabase
            .from('run_segments')
            .select('*')
            .eq('run_id', runId)
            .order('segment_order');

          if (segmentsError) {
            throw segmentsError;
          }

          setSegments(segmentsData || []);
        }
      } catch (err) {
        console.error('Error fetching run details:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch run details');
      } finally {
        setIsLoading(false);
      }
    };

    fetchRunDetails();
  }, [runId]);

  return { run, segments, isLoading, error };
};